// #664 D1/D8: a MIRROR of apps/backend/src/config/zodErrorMap.ts, so a form's own zod checks read
// as they did on zod 3 ("String must contain at most 50 character(s)", not zod 4's "Too big:
// expected string to have <=50 characters"). A schema's own message always wins; anything not
// covered returns undefined and keeps zod 4's text. Installed once in main.tsx (and the test setup).
//
// ⚠️ Change both copies together: the backend's tests/unit/config/zodErrorMap.parity.test.ts runs
// one table of cases through each and fails on any difference.
import { z, type core } from 'zod';

type Issue = core.$ZodRawIssue;

const typeName = (v: unknown): string =>
  v === null
    ? 'null'
    : Array.isArray(v)
      ? 'array'
      : typeof v === 'number'
        ? Number.isNaN(v)
          ? 'nan'
          : 'number'
        : v instanceof Date
          ? 'date'
          : typeof v;

const UNIT: Record<string, string> = { string: 'character(s)', array: 'element(s)' };

// The `expected` names zod 4 changed; every other name reads the same as on zod 3 (measured).
const ZOD3_TYPE_NAME: Record<string, string> = { record: 'object', tuple: 'array' };

export function plainZodMessage(iss: Issue): string | undefined {
  // (a) A missing value. On zod 4 a missing enum or literal is invalid_value, not invalid_type.
  if (iss.input === undefined && (iss.code === 'invalid_type' || iss.code === 'invalid_value')) {
    return 'Required';
  }
  // (b) The wrong type. `.int()` on zod 4 is a type check, not a refinement.
  if (iss.code === 'invalid_type') {
    if (iss.expected === 'int') {
      return Number.isFinite(iss.input)
        ? 'Expected integer, received float'
        : `Expected number, received ${typeName(iss.input)}`;
    }
    // zod 4 refuses these as a wrong type; zod 3 said "Invalid date" and (for .finite()) this.
    if (iss.expected === 'date' && iss.input instanceof Date) return 'Invalid date';
    if (iss.expected === 'number' && typeof iss.input === 'number' && !Number.isNaN(iss.input)) {
      return 'Number must be finite';
    }
    return `Expected ${ZOD3_TYPE_NAME[iss.expected] ?? iss.expected}, received ${typeName(iss.input)}`;
  }
  if (iss.code === 'too_small' || iss.code === 'too_big') {
    const n = iss.code === 'too_small' ? iss.minimum : iss.maximum;
    // (c) A string's length or an array's size; `.length()` sets `exact`.
    if (UNIT[iss.origin]) {
      const how = iss.exact ? 'exactly' : iss.code === 'too_small' ? 'at least' : 'at most';
      return `${iss.origin === 'string' ? 'String' : 'Array'} must contain ${how} ${n} ${UNIT[iss.origin]}`;
    }
    // (d) A number's bounds.
    if (iss.origin === 'number') {
      const side = iss.code === 'too_small' ? 'greater than' : 'less than';
      return `Number must be ${side}${iss.inclusive ? ' or equal to' : ''} ${n}`;
    }
  }
  // (e) An email address.
  if (iss.code === 'invalid_format' && iss.format === 'email') return 'Invalid email';
  return undefined;
}

/** Installs the map for every parse in the app. Idempotent (z.config replaces the setting). */
export function installZodErrorMap(): void {
  z.config({ customError: plainZodMessage });
}
